import {Component, EventEmitter, inject, Input, OnInit, Output, signal} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {OrganizationService} from '../../../../shared/services/organization.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Organization} from '../../../../shared/model/organization';
import {ApiResponse} from '../../../../shared/model/api-response';
import {AlertService} from '../../../../shared/services/alert.service';
import {GenericFormComponent} from '../../../../shared/components/generic-form/generic-form.component';
import {Messages} from '../../../../shared/constants/messages';
import {PermissionService} from '../../../../core/services/permission.service';
import {OrganizationMembersManagerComponent} from '../../../../shared/components/organization-members-manager/organization-members-manager.component';
import {UserService} from '../../../../shared/services/user.service';
import {AuthService} from '../../../../core/auth/auth.service';
import {LocationFormComponent} from '../../../../shared/components/location-form/location-form.component';
import {GeoLocationService} from '../../services/geo-location.service';

@Component({
  selector: 'app-organization-edit',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    GenericFormComponent,
    OrganizationMembersManagerComponent,
    LocationFormComponent
  ],
  templateUrl: './organization-edit.component.html',
  styleUrl: './organization-edit.component.css'
})
export class OrganizationEditComponent implements OnInit {

  /** Modalità embedded (es. modale admin): niente route né navigazione. */
  @Input() embeddedId?: number;
  @Output() saved = new EventEmitter<Organization | undefined>();
  @Output() cancelled = new EventEmitter<void>();

  editOrganizationForm: FormGroup;
  selectedOrganization = signal<Organization | null>(null);
  isEditMode = signal<boolean>(false);
  isLoading = signal<boolean>(false);

  private fb: FormBuilder = inject(FormBuilder);
  private organizationService: OrganizationService = inject(OrganizationService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private alertService: AlertService = inject(AlertService);
  private permissionService: PermissionService = inject(PermissionService);
  private userService: UserService = inject(UserService);
  private authService: AuthService = inject(AuthService);
  private geoLocationService: GeoLocationService = inject(GeoLocationService);

  constructor() {
    this.editOrganizationForm = this.fb.group({
      organizationName: ['', Validators.required],
      organizationDescription: [''],
      organizationLogo: [''],
      organizationWebsite: [''],
      organizationEmail: ['', Validators.email],
      organizationPhone: [''],
      location: this.fb.group({
        id: [''],
        formalName: [''],
        address: [''],
        city: [''],
        country: [''],
        town: [''],
        village: [''],
        neighbourhood: [''],
        residential: [''],
        postcode: [''],
        street: [''],
        houseNumber: [''],
        province: [''],
        latitude: [undefined],
        longitude: [undefined],
        rawInput: [''],
        displayName: [''],
        lat: [''],
        lon: [''],
      }),
      organizationActive: [true],
    });
  }

  ngOnInit(): void {
    const rawId = this.embeddedId ?? this.route.snapshot.paramMap.get('id');
    const orgId = rawId ? Number(rawId) : undefined;
    if (orgId) {
      this.isEditMode.set(true);
      this.loadOrganization(orgId);
      return;
    }

    if (!this.canCreate()) {
      this.editOrganizationForm.disable();
    }
  }

  loadOrganization(id: number) {
    this.isLoading.set(true);
    this.organizationService.getOrganizationById(id).subscribe({
      next: (response: ApiResponse<Organization>) => {
        this.isLoading.set(false);
        if (response.success && response.data) {
          if (!this.canOpenEdit(response.data)) {
            this.denyAccess();
            return;
          }
          this.selectedOrganization.set(response.data);
          this.patchForm(response.data);
          if (!this.canSave()) {
            this.editOrganizationForm.disable();
          }
        }
      },
      error: () => {
        this.isLoading.set(false);
        this.alertService.showError('Errore nel caricamento dell\'organizzazione');
        this.navigateAfterAction();
      }
    });
  }

  onSubmit() {
    if (!this.canSave() || this.editOrganizationForm.invalid) {
      return;
    }
    const formValue = this.editOrganizationForm.getRawValue();
    const organization: Partial<Organization> = {
      organizationName: formValue.organizationName,
      organizationDescription: formValue.organizationDescription || undefined,
      organizationLogo: formValue.organizationLogo || undefined,
      organizationWebsite: formValue.organizationWebsite || undefined,
      organizationEmail: formValue.organizationEmail || undefined,
      organizationPhone: formValue.organizationPhone || undefined,
      city: formValue.location?.city || undefined,
      province: formValue.location?.province || undefined,
      location: this.geoLocationService.toApiLocation(formValue.location) ?? undefined,
      organizationActive: formValue.organizationActive,
    };

    if (this.isEditMode() && this.selectedOrganization()) {
      const orgId = this.selectedOrganization()!.id;
      this.organizationService.updateOrganization(orgId, organization as Organization).subscribe({
        next: (response: ApiResponse<Organization>) => {
          if (response.success) {
            this.alertService.showSuccess(Messages.ORGANIZATION_UPDATED_SUCCESS);
            if (this.embeddedId) {
              this.saved.emit(response.data ?? undefined);
              return;
            }
            this.navigateAfterAction(orgId);
          }
        },
        error: () => {
          // Handled by global http interceptor alert; prevents unhandled RxJS error
        },
      });
    } else {
      this.organizationService.createOrganization(organization as Organization).subscribe({
        next: (response: ApiResponse<Organization>) => {
          if (response.success) {
            this.alertService.showSuccess(Messages.ORGANIZATION_CREATED_SUCCESS);
            this.refreshCurrentUser();
          }
        },
        error: () => {
          // Handled by global http interceptor alert; prevents unhandled RxJS error
        },
      });
    }
  }

  goBack() {
    this.navigateAfterAction(this.selectedOrganization()?.id);
  }

  canCreate(): boolean {
    return this.permissionService.canCreateOrganization();
  }

  canSave(): boolean {
    if (!this.isEditMode()) {
      return this.canCreate();
    }
    return this.permissionService.canEditOrg(this.selectedOrganization());
  }

  canOpenEdit(org: Organization): boolean {
    return this.permissionService.canEditOrg(org)
      || this.permissionService.canManageOrgMembers(org);
  }

  private patchForm(org: Organization) {
    const locationValue = this.geoLocationService.normalizeLocation(org.location);

    this.editOrganizationForm.patchValue({
      organizationName: org.organizationName,
      organizationDescription: org.organizationDescription || '',
      organizationLogo: org.organizationLogo || '',
      organizationWebsite: org.organizationWebsite || '',
      organizationEmail: org.organizationEmail || '',
      organizationPhone: org.organizationPhone || '',
      location: locationValue,
      organizationActive: org.organizationActive,
    });
  }

  private denyAccess() {
    this.alertService.showError(Messages.ORGANIZATION_MANAGER_ONLY);
    this.navigateAfterAction();
  }

  private refreshCurrentUser(): void {
    this.userService.getMyUserData().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.authService.setCurrentUser(response.data);
        }
        this.navigateAfterAction();
      },
      error: () => this.navigateAfterAction()
    });
  }

  private comesFromManage(): boolean {
    return this.route.snapshot.queryParamMap.get('from') === 'manage';
  }

  private navigateAfterAction(orgId?: number) {
    if (this.embeddedId) {
      this.cancelled.emit();
      return;
    }
    if (this.comesFromManage()) {
      this.router.navigate(['/organizations/manage']);
      return;
    }
    if (this.isEditMode() && orgId) {
      this.router.navigate(['/organizations/detail', orgId]);
      return;
    }
    this.router.navigate(['/organizations']);
  }
}
